import React from "react";
import { SkeletonItem, SkeletonItemProps } from "@fluentui/react-components";
import { ISpinnerProps, Spinner } from "@fluentui/react";

type BaseLoaderProps = {
  isLoading: boolean;
  children: React.ReactNode;
  style?: React.CSSProperties;
  "data-testid"?: string;
  type?: "skeleton" | "spinner";
};

type WithSkeletonLoaderProps = Omit<BaseLoaderProps, "type"> & {
  type?: "skeleton";
} & SkeletonItemProps;

type WithSpinnerLoaderProps = Omit<BaseLoaderProps, "type"> & {
  type: "spinner";
} & Omit<ISpinnerProps, "type">;

type Props = WithSkeletonLoaderProps | WithSpinnerLoaderProps;

export function WithLoader({ isLoading, children, ...props }: Props) {
  if (isLoading) {
    return getComponent(props);
  }

  return <>{children}</>;
}

function getComponent({
  type = "skeleton",
  style,
  "data-testid": testid,
  ...rest
}: Omit<Props, "isLoading" | "children">) {
  const loaderTypes: Record<NonNullable<BaseLoaderProps["type"]>, JSX.Element> = {
    skeleton: (
      <SkeletonItem
        {...(rest as SkeletonItemProps)}
        data-testid={testid}
        animation="pulse"
        style={style}
      />
    ),
    spinner: (
      <Spinner
        {...(rest as ISpinnerProps)}
        data-testid={testid}
        style={{ width: "fit-content", ...style }}
      />
    ),
  };

  return loaderTypes[type];
}
